import { OneOfDataElements, animatableProperties, OneOfElementPropertyKeys, ITransitionTemplate } from './nodes';
import { IKeyframe } from '../domain/keyframe';

export const transitionDirectionValues = {
    in: {
        left: 90,
        up: 180,
        right: 270,
        down: 0
    },
    out: {
        left: 270,
        up: 0,
        right: 90,
        down: 180
    }
};

export function getMainKeyframesOnElement(element: OneOfDataElements, property?: OneOfElementPropertyKeys): IKeyframe[] {
    const keyframes: IKeyframe[]  = [];
    for (const animatableProperty of (property ? [property] : animatableProperties)) {
        if (element[animatableProperty]) {
            const keyframesWithoutTransitions: IKeyframe[] = element[animatableProperty]!.filter(k => !k.transition);

            if (keyframesWithoutTransitions.length) {
                keyframes.push(
                    ...keyframesWithoutTransitions.map(k => {
                        k.property = animatableProperty;
                        return k;
                    })
                );
            }
        }
    }
    return keyframes;
}

export function getTransitionKeyframesOnElement(element: OneOfDataElements, property?: OneOfElementPropertyKeys): { in: IKeyframe[], out: IKeyframe[] } {
    const keyframes: { in: IKeyframe[], out: IKeyframe[] } = { in: [], out: [] };
    for (const animatableProperty of (property ? [property] : animatableProperties)) {
        if (element[animatableProperty]) {
            const keyframeWithTransitions: IKeyframe[] = element[animatableProperty]!.filter(k => {
                return k.transition !== undefined;
            });

            if (keyframeWithTransitions.length) {
                keyframes.in.push(
                    ...keyframeWithTransitions.filter(k => k.transition!.type === 'in').map(k => {
                        k.property = animatableProperty;
                        return k;
                    })
                );
                keyframes.out.push(...keyframeWithTransitions.filter(k => k.transition!.type === 'out').map(k => {
                    k.property = animatableProperty;
                    return k;
                }));
            }
        }
    }
    return keyframes;
}

export function getTransitionTypeAtTime(element: OneOfDataElements, time: number): 'in' | 'out' | undefined {
    const inDuration = getInTransitionDuration(element);
    if (inDuration && element.time <= time && time <= element.time + inDuration) {
        return 'in';
    }

    const outDuration = getOutTransitionDuration(element);
    if (outDuration) {
        const outStart = element.time + element.duration - outDuration;
        if (outStart <= time && time <= outStart + outDuration) {
            return 'out';
        }
    }

    return undefined;
}

export function getInTransitionDuration(element: OneOfDataElements): number | undefined {
    return getInTransitionEndTime(element);
}

export function getOutTransitionDuration(element: OneOfDataElements): number | undefined {
    const transitionKeyframes = getTransitionKeyframesOnElement(element)['out'];
    if (transitionKeyframes.length) {
        return element.duration - transitionKeyframes[0].time;
    }
    return undefined;
}

export function getInTransitionEndTime(element: OneOfDataElements): number | undefined {
    const transitionKeyframes = getTransitionKeyframesOnElement(element);
    if (transitionKeyframes.in.length) {
        return transitionKeyframes.in[transitionKeyframes.in.length - 1].time;
    }

    return undefined;
}

export function getOutTransitionStartTime(element: OneOfDataElements): number | undefined {
    const transitionKeyframes = getTransitionKeyframesOnElement(element);
    if (transitionKeyframes.out.length) {
        return transitionKeyframes.out[0].time;
    }

    return undefined;
}

export function isMainTransitionKeyframe(keyframe: IKeyframe, element: OneOfDataElements): boolean {
    if (keyframe && keyframe.transition) {
        const transitionKeyframes = getTransitionKeyframesOnElement(element);
        const type = keyframe.transition.type;
        const k = transitionKeyframes[keyframe.transition.type];

        if (type === 'in') {
            return k.indexOf(keyframe) === k.length - 1;
        } else {
            return k.indexOf(keyframe) === 0;
        }
    }

    return false;
}

export function getTemplateTransitionDuration(template: ITransitionTemplate): number {
    let duration = 0;

    for (const prop of template.animatedProperties) {
        for (const keyframe of prop.keyframes) {
            if (keyframe.time > duration) {
                duration = keyframe.time;
            }
        }
    }

    return duration;

}

export function isDataElementVisibleAtTime(element: OneOfDataElements, time: number): boolean {
    return !element.hidden && time >= element.time
            && time <= (element.time + element.duration);
}

export function getTimelineOverlap(position1: ITimelinePosition, position2: ITimelinePosition): ITimelinePosition | undefined {
    const from = Math.max(position1.time, position2.time);
    const to = Math.min(position1.time + position1.duration, position2.time + position2.duration);
    const duration = to - from;

    // Zero is no overlap
    if (duration > 0) {
        return {
            time: from,
            duration
        };
    }
}
export function getMainStateOverlap(element1: OneOfDataElements, element2: OneOfDataElements): ITimelinePosition | undefined {
    return getTimelineOverlap(getMainStateTimelinePosition(element1), getMainStateTimelinePosition(element2));
}

export function isTimelineOverlap(position1: ITimelinePosition, position2: ITimelinePosition): boolean {
    return getTimelineOverlap(position1, position2) !== undefined;
}

export function isMainStateOverlap(element1: OneOfDataElements, element2: OneOfDataElements): boolean {
    return isTimelineOverlap(getMainStateTimelinePosition(element1), getMainStateTimelinePosition(element2));
}



export function getMainStateTimelinePosition(element: OneOfDataElements): ITimelinePosition {
    const inDuration = getInTransitionDuration(element) || 0;
    const outDuration = getOutTransitionDuration(element) || 0;
    return {
        time: element.time + inDuration,
        duration: element.duration - (inDuration + outDuration)
    };
}

export function isDataElementInMainStateAtTime(element: OneOfDataElements, time: number): boolean {
    if (!isDataElementVisibleAtTime(element, time)) {
        return false;
    }
    const inDuration = getInTransitionDuration(element) || 0;
    const outDuration = getOutTransitionDuration(element) || 0;
    const inTime = element.time + inDuration;
    const outTime =  element.time + element.duration - outDuration;
    const tolerance = 0.000001;

    // Sometimes javascript gives you number like 7.6000000000000005 despite rounding.
    if (Math.abs(inTime - time) < tolerance || Math.abs(outTime - time) < tolerance) {
        return true;
    }
    // normal compare
    else if (time < inTime || time > outTime) {
        return false;
    }

    return true;
}

export interface ITimelinePosition {
    time: number;
    duration: number;
}
